import React from 'react';
import { motion } from 'motion/react';
import { MapPin, Clock, Globe, Smartphone } from 'lucide-react';
import { Card, CardContent } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { Link } from 'react-router-dom';

const ImageWithFallback = ({ src, alt, className }) => (
  <img src={src} alt={alt} className={className} />
);

// Example officeLocations prop
const officeLocations = [
  {
    country: 'Canada',
    city: 'Ontario',
    image: 'https://images.unsplash.com/photo-1517935706615-2717063c2225?w=400&h=250&fit=crop',
    address: '7169 Waters Niagara',
    timezone: 'EST (UTC -5)',
    email: 'info@metafoxtech.com',
    number: '+1-905-621-2191'
  },
  {
    country: 'Germany',
    city: 'Berlin',
    image: 'https://images.unsplash.com/photo-1546726747-421c6d69c929?w=400&h=250&fit=crop',
    address: 'Klenzepfad 40, Berlin 13407',
    timezone: 'CET (UTC +1)',
    email: 'info@metafoxtech.com',
    number: '+49-015510031462'
},
{
    country: 'India',
    city: 'Gujarat',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=250&fit=crop',
    address: 'A1/57 Pancham villa Ajwa road vadodara',
    timezone: 'IST (UTC +5)',
    email: 'info@metafoxtech.com',
    number: '+91-6352118120',
    number_two: '+91-7622854818'
  },
];

export default function OfficeLocationsSection() {
  return (
    <motion.div
      key="offices"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="container py-5"
    >
      {/* Header */}
      <div className="text-center mb-5">
        <Badge className="mb-4 bg-primary text-white">
          Global Presence
        </Badge>

        <h2
          className="fw-bold mb-3 text-primary">
          Our Office Locations
        </h2>

        <p className="fs-5 text-muted mx-auto fw-500" style={{ maxWidth: '40rem' }}>
          We operate globally with offices strategically located to serve our clients better.
        </p>
      </div>

      {/* Grid of offices */}
      <div className="row g-4">
        {officeLocations.map((office, index) => (
          <motion.div
            key={office.country}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
            whileHover={{ y: -10 }}
            className="col-12 col-lg-4"
          >
            <Card className="h-100 rounded-4 overflow-hidden cursor-pointer" style={{ backgroundColor: 'rgba(255 255 255 / 0.8)' }}>
              <div className="position-relative" style={{ height: '12rem', overflow: 'hidden' }}>
                <ImageWithFallback
                  src={office.image}
                  alt={`${office.city}, ${office.country}`}
                  className="w-100 h-100 object-fit-cover"
                  style={{ objectFit: 'cover' }}
                />
                <div
                  className="position-absolute top-0 start-0 w-100 h-100"
                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)' }}
                ></div>
                <Badge
                  className="position-absolute top-3 end-3 text-white bg-primary text-white"
                >
                  {office.country}
                </Badge>
              </div>

              <CardContent>
                <h3 className="h5 fw-bold mb-2 text-dark d-flex align-items-center">
                  <MapPin size={20} className="me-2 text-primary" />
                  {office.city}
                </h3>
                <p className="text-muted mb-3">{office.address}</p>

                <div className="mb-3">
                  <div className="d-flex align-items-center text-secondary small mb-1">
                    <Clock size={16} className="me-2" />
                    {office.timezone}
                  </div>
                  <div className="d-flex align-items-center text-secondary small">
                    <Globe size={16} className="me-2" />
                    {office.email}
                  </div>
                  <div className="d-flex align-items-center text-secondary small">
                    <Smartphone size={16} className='me-2' />
                    {office.number}
                  </div>
                  {office.number_two && <div className="d-flex align-items-center text-secondary small">
                    <Smartphone size={16} className='me-2' />
                    {office.number_two}
                  </div>}
                </div>
                <Link to={`tel:${office.number}`}
                  className="btn d-inline-flex align-items-center justify-center gap-2 text-sm fw-medium transition border-0 rounded 
                  bg-primary w-100 text-white rounded-3"
                  aria-label={`Contact ${office.city} Office`}
                >
                  Contact {office.city} Office
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}
